import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import '../Marcas/Marcas.css'; // Reusing Marcas styles for consistency
import './empresas.css'; // Custom specific styles
import MenuLateral from "../Components/MenuPrincipal/MenuLateral"; // Trocado para MenuLateral para teste visual
// import MenuPrincipal from "../Components/MenuPrincipal/MenuPrincipal"; // Comentado backup
import GridEmpresas from "../Components/EmpresasGrid/EmpresasGrid";
import Pagination from "../Components/Pagination/Pagination";
import Swal from 'sweetalert2';
import { useAuth } from "../Context/auth";

function Empresas() {
    const { currentUser, appUserData } = useAuth();
    const navigate = useNavigate();
    const [todasEmpresas, setTodasEmpresas] = useState([]);
    const [empresasFiltradas, setEmpresasFiltradas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [pesquisa, setPesquisa] = useState('');
    const [trigger, setTrigger] = useState(0);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Verifica permissão de leitura
    useEffect(() => {
        if (appUserData && !appUserData['ler_empresas']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    // Estado da Sidebar
    const [showFiltros, setShowFiltros] = useState(false);

    // Estado dos Filtros (Formulário)
    const [filtrosForm, setFiltrosForm] = useState({
        is_cliente: true,
        is_fornecedor: true,
        is_transportadora: true,
        is_terceiro: true,
        cpf_cnpj: '',
        nome: '',
        nome_fantasia: '',
        uf: '',
        cidade: '',
        bairro: '',
        telefone: ''
    });

    // Estado dos Filtros (Aplicados na Lista)
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        is_cliente: true,
        is_fornecedor: true,
        is_transportadora: true,
        is_terceiro: true,
        cpf_cnpj: '',
        nome: '',
        nome_fantasia: '',
        uf: '',
        cidade: '',
        bairro: '',
        telefone: ''
    });

    useEffect(() => {
        const fetchEmpresas = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/empresas`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error("Falha ao carregar empresas.");
                const data = await response.json();
                setTodasEmpresas(data);
                setEmpresasFiltradas(data);
            } catch (error) {
                setErro('Ocorreu um erro ao carregar a lista de empresas.');
            } finally {
                setLoading(false);
            }
        };
        fetchEmpresas();
    }, [currentUser, trigger]);

    // Reseta página ao pesquisar
    useEffect(() => {
        setCurrentPage(1);
    }, [pesquisa, filtrosAplicados]);

    // Lógica de Filtragem Unificada
    useEffect(() => {
        let resultado = [...todasEmpresas];

        // 1. Filtro Global
        const termoGlobal = pesquisa.trim().toLowerCase();
        if (termoGlobal) {
            resultado = resultado.filter(empresa => {
                const nome = (empresa.nome || '').toLowerCase();
                const fantasia = (empresa.nome_fantasia || '').toLowerCase();
                const cpfCnpj = (empresa.cpf_cnpj || '').replace(/\D/g, '');

                const termoNumerico = termoGlobal.replace(/\D/g, '');
                const matchTexto = nome.includes(termoGlobal) || fantasia.includes(termoGlobal);
                const matchDoc = termoNumerico ? cpfCnpj.includes(termoNumerico) : false;

                return matchTexto || matchDoc;
            });
        }

        // 2. Filtros Avançados
        // Categorias (OR logic)
        resultado = resultado.filter(empresa => {
            if (filtrosAplicados.is_cliente && empresa.is_cliente) return true;
            if (filtrosAplicados.is_fornecedor && empresa.is_fornecedor) return true;
            if (filtrosAplicados.is_transportadora && empresa.is_transportadora) return true;
            if (filtrosAplicados.is_terceiro && empresa.is_terceiro) return true;
            return false;
        });

        // Campos Específicos (AND logic)
        if (filtrosAplicados.nome) {
            const fNome = filtrosAplicados.nome.toLowerCase();
            resultado = resultado.filter(e => (e.nome || '').toLowerCase().includes(fNome));
        }
        if (filtrosAplicados.nome_fantasia) {
            const fFantasia = filtrosAplicados.nome_fantasia.toLowerCase();
            resultado = resultado.filter(e => (e.nome_fantasia || '').toLowerCase().includes(fFantasia));
        }
        if (filtrosAplicados.cpf_cnpj) {
            const fDoc = filtrosAplicados.cpf_cnpj.replace(/\D/g, '');
            resultado = resultado.filter(e => (e.cpf_cnpj || '').replace(/\D/g, '').includes(fDoc));
        }
        if (filtrosAplicados.uf) {
            const fUF = filtrosAplicados.uf.toLowerCase();
            resultado = resultado.filter(e => (e.uf || '').toLowerCase().includes(fUF));
        }
        if (filtrosAplicados.cidade) {
            const fCidade = filtrosAplicados.cidade.toLowerCase();
            resultado = resultado.filter(e => (e.cidade || '').toLowerCase().includes(fCidade));
        }
        if (filtrosAplicados.bairro) {
            const fBairro = filtrosAplicados.bairro.toLowerCase();
            resultado = resultado.filter(e => (e.bairro || '').toLowerCase().includes(fBairro));
        }
        if (filtrosAplicados.telefone) {
            const fTel = filtrosAplicados.telefone.replace(/\D/g, '');
            resultado = resultado.filter(e => (e.telefone || '').replace(/\D/g, '').includes(fTel));
        }

        setEmpresasFiltradas(resultado);
    }, [pesquisa, filtrosAplicados, todasEmpresas]);

    // Lógica de Paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEmpresas = empresasFiltradas.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    async function deleteEmpresa(id) {
        // Validation for Delete
        if (appUserData && appUserData['excluir_empresas'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const result = await Swal.fire({ title: "Você tem certeza?", text: "Esta ação não poderá ser revertida!", icon: "warning", showCancelButton: true, confirmButtonColor: "#2185c6", cancelButtonColor: "#DC3545", confirmButtonText: "Sim, excluir!", cancelButtonText: "Cancelar" });
        if (result.isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/empresas/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error);
                }
                Swal.fire("Excluído!", "A empresa foi deletada com sucesso.", "success");
                setTrigger(Date.now());
            } catch (error) {
                Swal.fire("Erro!", error.message, "error");
            }
        }
    }

    const handleFiltroChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFiltrosForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAplicarFiltros = () => {
        setFiltrosAplicados(filtrosForm);
        setShowFiltros(false);
    };

    const handleKeyDownFiltros = (e) => {
        if (e.key === 'Enter') {
            handleAplicarFiltros();
        }
    };

    const handleLimparFiltros = () => {
        const resetState = {
            is_cliente: true,
            is_fornecedor: true,
            is_transportadora: true,
            is_terceiro: true,
            cpf_cnpj: '',
            nome: '',
            nome_fantasia: '',
            uf: '',
            cidade: '',
            bairro: '',
            telefone: ''
        };
        setPesquisa('');
        setFiltrosForm(resetState);
        setFiltrosAplicados(resetState);
    };

    return (
        <div className="marcas-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            {/* <MenuPrincipal /> */}
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="marcas-header">
                    <h1 className="marcas-title">
                        <i className="fas fa-building text-primary"></i>
                        Listagem de <span className="highlight">Empresas</span>
                    </h1>
                </div>

                <main className="marcas-content" style={{ maxWidth: '98%' }}>
                    <div className="marcas-controls">
                        <Link
                            to="/app/novaempresa"
                            className="btn-primary-custom"
                            onClick={(e) => {
                                // Validation for Create
                                if (appUserData && appUserData['cadastrar_empresas'] === false) {
                                    e.preventDefault();
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                }
                            }}
                        >
                            <i className="fas fa-plus"></i>Nova Empresa
                        </Link>
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por nome, fantasia ou CPF/CNPJ..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                            />
                        </div>

                        <button
                            className="btn-icon-custom position-relative"
                            onClick={() => setShowFiltros(true)}
                            title="Filtros Avançados"
                        >
                            <i className="fas fa-filter"></i>
                            {(filtrosAplicados.nome || filtrosAplicados.cpf_cnpj || filtrosAplicados.cidade || filtrosAplicados.uf || filtrosAplicados.bairro || filtrosAplicados.telefone || !filtrosAplicados.is_cliente || !filtrosAplicados.is_fornecedor || !filtrosAplicados.is_transportadora || !filtrosAplicados.is_terceiro) && (
                                <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
                                    <span className="visually-hidden">Filtros ativos</span>
                                </span>
                            )}
                        </button>
                        <button className="btn-icon-custom ms-2" onClick={() => setTrigger(Date.now())} title="Atualizar Lista">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>

                    {erro && (
                        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <div>{erro}</div>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <GridEmpresas arrayEmpresas={currentEmpresas} clickDelete={deleteEmpresa} userPermissions={appUserData} />

                            <Pagination
                                totalItems={empresasFiltradas.length}
                                itemsPerPage={itemsPerPage}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </main>
            </div>

            {/* --- SIDEBAR DE FILTROS AVANÇADOS --- */}
            <Offcanvas show={showFiltros} onHide={() => setShowFiltros(false)} placement="end" style={{ zIndex: 10500 }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title><i className="fas fa-filter me-2"></i>Filtros Avançados</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="d-grid gap-3" onKeyDown={handleKeyDownFiltros}>

                        <div className="d-grid gap-2 mb-3">
                            <button className="btn btn-primary-custom" onClick={handleAplicarFiltros}>
                                <i className="fas fa-check me-2"></i>Aplicar Filtros
                            </button>
                            <button className="btn btn-outline-secondary" onClick={handleLimparFiltros}>
                                <i className="fas fa-eraser me-2"></i>Limpar Filtros
                            </button>
                        </div>
                        <hr className="m-0" />

                        {/* 1. Por Categoria */}
                        <div className="card bg-light border-0">
                            <div className="card-body">
                                <h6 className="card-subtitle mb-2 text-muted fw-bold">Categorias</h6>
                                <div className="d-flex flex-column gap-2">
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" name="is_cliente" id="checkFiltroCliente" checked={filtrosForm.is_cliente} onChange={handleFiltroChange} />
                                        <label className="form-check-label ms-2" htmlFor="checkFiltroCliente">Cliente</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" name="is_fornecedor" id="checkFiltroFornecedor" checked={filtrosForm.is_fornecedor} onChange={handleFiltroChange} />
                                        <label className="form-check-label ms-2" htmlFor="checkFiltroFornecedor">Fornecedor</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" name="is_transportadora" id="checkFiltroTransportadora" checked={filtrosForm.is_transportadora} onChange={handleFiltroChange} />
                                        <label className="form-check-label ms-2" htmlFor="checkFiltroTransportadora">Transportadora</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" name="is_terceiro" id="checkFiltroTerceiro" checked={filtrosForm.is_terceiro} onChange={handleFiltroChange} />
                                        <label className="form-check-label ms-2" htmlFor="checkFiltroTerceiro">Terceiro</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Filtros de Texto */}
                        <div>
                            <label className="form-label small fw-bold">CPF / CNPJ</label>
                            <input type="text" className="form-control" name="cpf_cnpj" value={filtrosForm.cpf_cnpj} onChange={handleFiltroChange} placeholder="Somente números..." />
                        </div>

                        <div>
                            <label className="form-label small fw-bold">Nome / Razão Social</label>
                            <input type="text" className="form-control" name="nome" value={filtrosForm.nome} onChange={handleFiltroChange} placeholder="Nome da empresa..." />
                        </div>

                        <div>
                            <label className="form-label small fw-bold">Apelido / Nome Fantasia</label>
                            <input type="text" className="form-control" name="nome_fantasia" value={filtrosForm.nome_fantasia} onChange={handleFiltroChange} placeholder="Nome fantasia..." />
                        </div>

                        <div className="row g-2">
                            <div className="col-4">
                                <label className="form-label small fw-bold">UF</label>
                                <input type="text" className="form-control" name="uf" value={filtrosForm.uf} onChange={handleFiltroChange} placeholder="UF" maxLength="2" />
                            </div>
                            <div className="col-6">
                                <label className="form-label small fw-bold">Cidade</label>
                                <input type="text" className="form-control" name="cidade" value={filtrosForm.cidade} onChange={handleFiltroChange} placeholder="Cidade" />
                            </div>
                        </div>

                        <div>
                            <label className="form-label small fw-bold">Bairro</label>
                            <input type="text" className="form-control" name="bairro" value={filtrosForm.bairro} onChange={handleFiltroChange} placeholder="Bairro..." />
                        </div>

                        <div>
                            <label className="form-label small fw-bold">Telefone</label>
                            <input type="text" className="form-control" name="telefone" value={filtrosForm.telefone} onChange={handleFiltroChange} placeholder="Parte do telefone..." />
                        </div>



                    </div>
                </Offcanvas.Body>
            </Offcanvas>

        </div>
    );
}
export default Empresas;